import { Outlet, useNavigate, NavLink, Navigate } from "react-router-dom";
import { LayoutDashboard, ClipboardList, LogOut, ShieldCheck, Loader2, Wallet, Users as UsersIcon, LifeBuoy, LineChart, Settings, FileClock, Sparkles } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const NAV_ITEMS = [
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard, testid: "admin-nav-overview" },
  { to: "/admin/campaigns", label: "Campaigns", icon: ClipboardList, testid: "admin-nav-campaigns" },
  { to: "/admin/finance", label: "Finance", icon: Wallet, testid: "admin-nav-finance" },
  { to: "/admin/users", label: "Users", icon: UsersIcon, testid: "admin-nav-users" },
  { to: "/admin/early-access", label: "Early Access", icon: Sparkles, testid: "admin-nav-early-access" },
  { to: "/admin/support", label: "Support", icon: LifeBuoy, testid: "admin-nav-support" },
  { to: "/admin/analytics", label: "Analytics", icon: LineChart, testid: "admin-nav-analytics" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
  { to: "/admin/audit-log", label: "Audit log", icon: FileClock, testid: "admin-nav-audit" },
];

const AdminLayout = () => {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#FFFBF5]"><Loader2 className="h-6 w-6 animate-spin text-[#0D1B2A]" /></div>;
  }
  if (!admin) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-[#FFFBF5] text-[#0D1B2A] font-sans flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-[#0D1B2A]/10 bg-white">
        <div className="h-16 px-6 flex items-center gap-2 border-b border-[#0D1B2A]/10">
          <span className="grid place-items-center h-8 w-8 rounded-xl bg-[#0D1B2A] text-[#E85D04]">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <div className="font-display font-semibold leading-none">Dwinov</div>
            <div className="text-[10px] uppercase tracking-widest text-[#E85D04] mt-0.5">Admin</div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive
                  ? "bg-[#0D1B2A] text-white"
                  : "text-[#0D1B2A]/70 hover:text-[#0D1B2A] hover:bg-[#0D1B2A]/5"}`
              }
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#0D1B2A]/10">
          <div className="text-sm font-medium truncate">{admin.name}</div>
          <div className="text-xs text-[#0D1B2A]/60 truncate">{admin.email}</div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#E85D04] font-semibold">
            {admin.role === "super_admin" ? "Super Admin" : "Support Staff"}
          </div>
          <button
            onClick={async () => { await logout(); navigate("/admin", { replace: true }); }}
            data-testid="admin-logout-btn"
            className="mt-4 w-full inline-flex items-center gap-2 justify-center px-3 py-2 rounded-xl text-sm font-medium border border-[#0D1B2A]/15 text-[#0D1B2A]/80 hover:bg-[#0D1B2A]/5 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden h-16 border-b border-[#0D1B2A]/10 bg-white flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-8 w-8 rounded-xl bg-[#0D1B2A] text-[#E85D04]"><ShieldCheck className="h-4 w-4" /></span>
            <span className="font-display font-semibold">Dwinov <span className="text-[#E85D04] text-xs">Admin</span></span>
          </div>
          <button onClick={logout} className="text-sm font-medium text-[#0D1B2A]/70">Sign out</button>
        </header>
        <div className="md:hidden flex px-4 py-2 border-b border-[#0D1B2A]/10 bg-white gap-2 overflow-x-auto">
          {NAV_ITEMS.map((n) => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${isActive ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 text-[#0D1B2A]/70"}`
              }
            >{n.label}</NavLink>
          ))}
        </div>
        <main className="flex-1 overflow-auto"><Outlet /></main>
      </div>
    </div>
  );
};

export default AdminLayout;
